import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';
import { getTheme } from '../theme';
import TicketDetail from './TicketDetail';
import SmsConversation from './SmsConversation';
import { ContactEdit, ConfirmTextLine, NoPhoneHint } from './BookingContact';
import { toast, toastNotified, toastPhone } from '../utils/toast';
import CustomerMatch from './CustomerMatch';

// ── Rentals calendar, under Bookings (4 Oct) ───────────────────────────────────
//
// A row per bike and a bar per rental over two weeks. Click a rental for its
// riders, money and actions: start the damage hold, check the bikes out, check
// them in (release the hold or charge damage), change dates, cancel. Demo
// credits sit underneath. Everything comes from /book/rent/staff/*.

const DAY_PX = 64;
const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };
const weekday = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };
const dayCount = (a, b) => { const f = (s) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); }; return Math.round((f(b) - f(a)) / 86400000) + 1; };
const lab = (s, o) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', o || { weekday: 'short', month: 'short', day: 'numeric' }); };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtPhone = (p) => { const d = String(p || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || ''); };
const clock = (hhmm) => { const [h, m] = String(hhmm || '').split(':').map(Number); return Number.isFinite(h) ? `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}` : ''; };
const first = (n) => (n || '').trim().split(/\s+/)[0] || 'the customer';

function stateOf(r, today) {
  if (r.status === 'returned') return 'returned';
  if (r.status === 'out') return r.end < today ? 'late' : 'out';
  return 'booked';
}
const STATE = {
  booked: { label: 'Booked', bar: (T) => ({ background: T.card, border: '2px solid #e67e22', color: T.text }) },
  out: { label: 'Out', bar: () => ({ background: '#2a6db0', border: '2px solid #2a6db0', color: '#fff' }) },
  late: { label: 'Late', bar: () => ({ background: '#c0392b', border: '2px solid #c0392b', color: '#fff' }) },
  returned: { label: 'Returned', bar: (T, dark) => ({ background: dark ? '#2a3550' : '#e8e8e2', border: `2px solid ${dark ? '#334063' : '#d8d8d0'}`, color: T.textSub }) },
};
const HOLD_TEXT = {
  none: 'not started', pending: 'link sent, waiting for the card', held: 'held on the card', charged: 'charged (refunded on return)',
  released: 'released', captured: 'damage charged', refunded: 'refunded', kept_part: 'part kept for damage', not_needed: 'none needed',
};

export default function RentalsCalendar({ theme = 'dark', currentUser = null, focus = null }) {
  const isDark = theme === 'dark';
  const T = { ...getTheme(theme), ...(isDark ? { textSub: '#8a98b5' } : {}) };
  // Opened from the Overview: the fortnight starting the Monday of its pick-up.
  const [from, setFrom] = useState(focus && focus.date ? addDays(focus.date, -((weekday(focus.date) + 6) % 7)) : null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(focus ? focus.id : null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = from ? `?from=${from}&to=${addDays(from, 13)}` : '';
      const r = await fetch(`${API}/book/rent/staff/calendar${q}`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load rentals.');
      setData(d); setError('');
      if (!from) setFrom(addDays(d.today, -((weekday(d.today) + 6) % 7)));
    } catch (err) { setError(err.message); }
  }, [from]);
  useEffect(() => { load(); }, [load]);

  const api = async (method, url, body) => {
    const r = await fetch(`${API}${url}`, { method, credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(d.error || 'That did not work.'), { data: d });
    return d;
  };
  // Rentals whose hold link was texted this visit, with when: the button then
  // asks before texting again (6 Oct: three texts from three clicks).
  const [holdTexted, setHoldTexted] = useState({});
  const [sendingHold, setSendingHold] = useState(null);
  const run = async (fn) => { setBusy(true); setError(''); try { await fn(); await load(); } catch (err) { setError(err.message); } setBusy(false); };

  const days = useMemo(() => (from ? Array.from({ length: 14 }, (_, i) => addDays(from, i)) : []), [from]);
  if (!data || !from) return <div style={{ color: T.textSub, padding: 40 }} role={error ? 'alert' : undefined}>{error || 'Loading rentals…'}</div>;

  const today = data.today;
  const to = days[days.length - 1];
  const selected = data.rentals.find(r => r.id === selectedId) || null;
  const btn = (primary, extra) => ({ ...S.btn(primary), fontSize: 13, padding: '7px 14px', ...(primary ? { background: '#e67e22', color: '#fff' } : { color: T.text, borderColor: T.inputBorder }), ...extra });
  const legend = Object.entries(STATE).map(([k, v]) => (
    <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSub }}>
      <span style={{ width: 16, height: 12, borderRadius: 3, ...v.bar(T, isDark), borderWidth: 2 }} />{v.label}
    </span>
  ));

  // Bars for one bike row.
  const barsFor = (bike) => {
    const out = [];
    for (const r of data.rentals) {
      const mine = r.bikes.find(b => b.bikeId === bike.id);
      if (!mine) continue;
      const st = stateOf(r, today);
      const s = r.start < from ? from : r.start;
      const realEnd = st === 'late' ? (today > to ? to : today) : r.end;
      const e = realEnd > to ? to : realEnd;
      if (e < from || s > to) continue;
      const left = (dayCount(from, s) - 1) * DAY_PX + 3;
      const width = dayCount(s, e) * DAY_PX - 6;
      const sel = r.id === selectedId;
      out.push(
        <div key={r.id} role="button" tabIndex={0} onClick={() => setSelectedId(sel ? null : r.id)} onKeyDown={ev => { if (ev.key === 'Enter') setSelectedId(sel ? null : r.id); }}
          title={`${r.customerName} · ${STATE[st].label}`}
          style={{ position: 'absolute', left, top: 6, width, height: 44, borderRadius: 8, padding: '4px 8px', boxSizing: 'border-box', overflow: 'hidden', cursor: 'pointer',
            ...STATE[st].bar(T, isDark), boxShadow: sel ? '0 0 0 3px #e67e2266' : 'none' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{width < 100 ? first(r.customerName) : r.customerName}</div>
          <div style={{ fontSize: 11, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {st === 'late' ? `Late, due ${lab(r.end, { weekday: 'short' })}` : st === 'out' ? `Out · back ${r.end === today ? 'today' : lab(r.end, { weekday: 'short' })}` : STATE[st].label}{mine.demo ? ' · demo' : r.bikes.length > 1 ? ` · ${r.bikes.length} bikes` : ''}
          </div>
        </div>
      );
    }
    return out;
  };

  const lateOnes = data.rentals.filter(r => stateOf(r, today) === 'late');
  const backToday = data.rentals.filter(r => r.status === 'out' && r.end === today);
  const goingOut = data.rentals.filter(r => r.status === 'confirmed' && r.start === today);

  const detail = selected && (() => {
    const r = selected;
    const st = stateOf(r, today);
    const due = Math.max(0, r.total - (['captured', 'authorized'].includes(r.paymentStatus) ? r.paid : 0));
    const holdOk = ['held', 'charged'].includes(r.hold.status) || !(r.hold.amount > 0);
    const row = (k, v) => v ? <><span style={{ color: T.textSub }}>{k}</span><span style={{ color: T.text }}>{v}</span></> : null;
    return (
      <div style={{ width: 340, flexShrink: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {r.workorderId && <span style={{ fontSize: 12, fontWeight: 700, color: '#1A2535', background: '#E67E22', borderRadius: 5, padding: '2px 7px' }}>SVC-{r.workorderId}</span>}
          <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: st === 'late' ? '#c0392b22' : '#e67e2222', color: st === 'late' ? '#c0392b' : '#c0661a' }}>{STATE[st].label}</span>
          {r.source === 'staff' && <span style={{ fontSize: 11, color: T.textSub }}>phone booking</span>}
          <button type="button" onClick={() => setSelectedId(null)} title="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>{r.customerName}</div>
            {r.status !== 'canceled' && <button type="button" onClick={() => setModal({ kind: 'contact', r })} style={{ background: 'transparent', border: 'none', padding: 0, color: '#e67e22', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Edit</button>}
          </div>
          {r.customerPhone && <div style={{ fontSize: 13, color: T.textSub }}>{fmtPhone(r.customerPhone)}</div>}
          {r.customerEmail && <div style={{ fontSize: 13, color: T.textSub }}>{r.customerEmail}</div>}
          <ConfirmTextLine T={T} confirmText={r.confirmText} phone={r.customerPhone} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: '4px 8px', fontSize: 13 }}>
          {row('Dates', `${lab(r.start)} to ${lab(r.end)} · ${dayCount(r.start, r.end)} days`)}
          {row('Pick up', `${lab(r.start, { weekday: 'short' })} from ${clock(data.rules.pickupTime)}`)}
          {row('Total', `${money(r.total)}${r.paid ? ` · ${money(r.paid)} ${r.paymentStatus === 'authorized' ? 'held' : 'paid'}` : ''}${r.status === 'confirmed' ? ` · ${money(due)} due at pick-up` : ''}`)}
          {r.hold.amount > 0 && row(r.hold.mode === 'charge' ? 'Deposit' : 'Hold', `${money(r.hold.amount)}, ${HOLD_TEXT[r.hold.status || 'none'] || r.hold.status}`)}
          {r.damageCharged ? row('Damage', money(r.damageCharged)) : null}
        </div>
        <div style={{ background: isDark ? '#1e2a40' : '#f4f4ef', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textSub, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>Riders</div>
          {r.bikes.map((b, i) => (
            <div key={i} style={{ color: T.text }}><b>{b.name}</b>{b.demo ? ' (demo)' : ''}: {b.rider?.name || 'no name given'}
              {(b.rider?.answers || []).length > 0 && <span style={{ color: T.textSub }}>, {(b.rider.answers).map(a => a.answer).join(', ')}</span>}</div>
          ))}
          {r.notes && <div style={{ color: T.text, marginTop: 4, fontStyle: 'italic' }}>"{r.notes}"</div>}
        </div>

        {r.status === 'confirmed' && r.hold.amount > 0 && !holdOk && (
          <div style={{ border: `1px dashed ${T.inputBorder}`, borderRadius: 8, padding: 10, display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12.5, color: T.text }}>Before the bikes go out: the {money(r.hold.amount)} damage {r.hold.mode === 'charge' ? 'deposit' : 'hold'}.</div>
            {holdTexted[r.id] && <div style={{ fontSize: 12, color: '#1e8449', fontWeight: 600 }}>✓ Link texted at {new Date(holdTexted[r.id]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}. Waiting for the card.</div>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" disabled={busy || !r.customerPhone} style={btn(false, { fontSize: 12, padding: '5px 10px', opacity: busy ? 0.6 : 1 })}
                onClick={() => {
                  if (holdTexted[r.id] && !window.confirm(`The link was already texted at ${new Date(holdTexted[r.id]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}. Text it again?`)) return;
                  setSendingHold(r.id);
                  run(async () => {
                    try {
                      const d = await api('POST', `/book/rent/staff/${r.id}/hold`, { send: true });
                      if (d.texted) { setHoldTexted(x => ({ ...x, [r.id]: Date.now() })); toast(`Hold link texted to ${(r.customerName || '').split(' ')[0]} at ${toastPhone(d.to || r.customerPhone)}.`); }
                      else toast(`The link was not texted${d.textNote ? `: ${d.textNote}` : '.'} Open it on this computer instead.`, 'error');
                    } finally { setSendingHold(null); }
                  });
                }}>{sendingHold === r.id ? 'Sending…' : holdTexted[r.id] ? 'Text it again' : 'Text the link'}</button>
              <button type="button" disabled={busy} style={btn(false, { fontSize: 12, padding: '5px 10px' })}
                onClick={() => run(async () => { const d = await api('POST', `/book/rent/staff/${r.id}/hold`, {}); window.open(d.link, '_blank', 'noopener'); })}>Open on this computer</button>
              {r.hold.status === 'pending' && <button type="button" disabled={busy} style={btn(false, { fontSize: 12, padding: '5px 10px' })}
                onClick={() => run(() => api('GET', `/book/rent/staff/${r.id}/hold`))}>Check again</button>}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {r.status === 'confirmed' && (
            <button type="button" disabled={busy} style={btn(true, { gridColumn: '1 / 3' })}
              onClick={() => {
                if (!holdOk && !window.confirm(`The damage hold is not in place. Let the bikes go out without it?`)) return;
                run(() => api('POST', `/book/rent/staff/${r.id}/checkout`, { withoutHold: !holdOk }));
              }}>Check out bikes</button>
          )}
          {r.status === 'out' && <button type="button" disabled={busy} style={btn(true, { gridColumn: '1 / 3' })} onClick={() => setModal({ kind: 'checkin', r })}>Check in bikes</button>}
          <button type="button" disabled={!r.workorderId} style={btn(false, { opacity: r.workorderId ? 1 : 0.5 })} onClick={() => setModal({ kind: 'ticket', r })}>Open ticket</button>
          <button type="button" disabled={!r.customerPhone} style={btn(false, { opacity: r.customerPhone ? 1 : 0.5 })} onClick={() => setModal({ kind: 'text', r })}>Text customer</button>
          {['confirmed', 'out'].includes(r.status) && <button type="button" style={btn(false)} onClick={() => setModal({ kind: 'dates', r })}>{r.status === 'out' ? 'Change return' : 'Change dates'}</button>}
          {r.status === 'confirmed' && <button type="button" disabled={busy} style={btn(false, { color: '#e74c3c', borderColor: '#e74c3c44' })}
            onClick={() => {
              let msg = `Cancel ${r.customerName}'s rental? The Lightspeed ticket moves to your cancellation status (it is not deleted).`;
              if (r.paid) msg += ` Their ${money(r.paid)} deposit is refunded.`;
              if (['held', 'charged', 'pending'].includes(r.hold.status)) msg += ' The damage hold is released.';
              if (!window.confirm(`${msg}\n\nThey get a text and an email.`)) return;
              run(async () => { const d = await api('PUT', `/book/staff/${r.id}/status`, { status: 'canceled' }); setSelectedId(null); toastNotified(r.customerName, d.notified, 'the cancellation'); });
            }}>Cancel</button>}
        </div>
      </div>
    );
  })();

  const activeCredits = data.demoCredits.filter(c => !c.usedAt && c.expiresOn >= today);
  const pastCredits = data.demoCredits.filter(c => c.usedAt || c.expiresOn < today);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" style={btn(false, { padding: '7px 12px' })} onClick={() => setFrom(addDays(from, -7))} aria-label="Earlier">‹</button>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.text, minWidth: 200, textAlign: 'center' }}>{lab(from, { month: 'short', day: 'numeric' })} to {lab(to, { month: 'short', day: 'numeric' })}</div>
        <button type="button" style={btn(false, { padding: '7px 12px' })} onClick={() => setFrom(addDays(from, 7))} aria-label="Later">›</button>
        <button type="button" style={btn(false)} onClick={() => setFrom(addDays(today, -((weekday(today) + 6) % 7)))}>Today</button>
        <span style={{ display: 'flex', gap: 14, marginLeft: 8, flexWrap: 'wrap' }}>{legend}</span>
        <button type="button" style={btn(true, { marginLeft: 'auto' })} disabled={!data.fleet.length} onClick={() => setModal({ kind: 'book' })}>+ Book a rental</button>
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginBottom: 10 }}>{error}</div>}
      {!data.fleet.length && <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>No bikes in the fleet yet. Add them in Settings, Online Booking, Rentals and demos.</div>}

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 220 + 14 * DAY_PX }}>
              <div style={{ display: 'flex', borderBottom: `1px solid ${T.cardBorder}` }}>
                <div style={{ width: 220, padding: '10px 14px', fontSize: 12, fontWeight: 700, color: T.textSub, letterSpacing: '0.06em', textTransform: 'uppercase', alignSelf: 'flex-end', flexShrink: 0 }}>Bike</div>
                {days.map(d => (
                  <div key={d} style={{ width: DAY_PX, flexShrink: 0, textAlign: 'center', padding: '8px 0', borderLeft: `1px solid ${T.cardBorder}`, background: d === today ? '#e67e2218' : 'transparent' }}>
                    <div style={{ fontSize: 11, color: d === today ? '#e67e22' : T.textSub, fontWeight: d === today ? 700 : 400 }}>{lab(d, { weekday: 'short' })}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: d === today ? '#e67e22' : T.text }}>{Number(d.slice(8))}</div>
                  </div>
                ))}
              </div>
              {data.fleet.map(bike => (
                <div key={bike.id} style={{ display: 'flex', borderBottom: `1px solid ${T.cardBorder}` }}>
                  <div style={{ width: 220, padding: '10px 14px', flexShrink: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{bike.name}{bike.demo && <span style={{ fontSize: 10.5, marginLeft: 6, color: '#c0661a' }}>DEMO</span>}</div>
                    <div style={{ fontSize: 12, color: T.textSub }}>{bike.size ? `Size ${bike.size}` : ''}{bike.inService ? ' · in for service' : bike.online === false ? ' · not online' : ''}</div>
                  </div>
                  <div style={{ position: 'relative', display: 'flex', background: bike.inService ? (isDark ? 'repeating-linear-gradient(135deg, rgba(255,255,255,0.03) 0 8px, rgba(255,255,255,0.07) 8px 16px)' : 'repeating-linear-gradient(135deg,#f4f4ef 0 8px,#ebebe5 8px 16px)') : 'transparent' }}>
                    {days.map(d => <div key={d} style={{ width: DAY_PX, height: 56, flexShrink: 0, borderLeft: `1px solid ${T.cardBorder}`, background: d === today ? '#e67e220f' : 'transparent' }} />)}
                    {barsFor(bike)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {(lateOnes.length > 0 || backToday.length > 0 || goingOut.length > 0) && (
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: T.textSub, borderBottom: `1px solid ${T.cardBorder}` }}>
              Today: {goingOut.length > 0 && <><b style={{ color: T.text }}>{goingOut.map(r => first(r.customerName)).join(', ')}</b> pick{goingOut.length === 1 ? 's' : ''} up. </>}
              {backToday.length > 0 && <><b style={{ color: T.text }}>{backToday.map(r => first(r.customerName)).join(', ')}</b> bring{backToday.length === 1 ? 's' : ''} bikes back. </>}
              {lateOnes.length > 0 && <><b style={{ color: '#c0392b' }}>{lateOnes.map(r => first(r.customerName)).join(', ')}</b> {lateOnes.length === 1 ? 'is' : 'are'} late.</>}
            </div>
          )}
          <div style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.textSub, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Demo credits</div>
            {activeCredits.length + pastCredits.length === 0 && <div style={{ fontSize: 13, color: T.textSub }}>None yet. A demo leaves a credit when its bike comes back.</div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {[...activeCredits, ...pastCredits].map(c => {
                const left = dayCount(today, c.expiresOn) - 1;
                const live = !c.usedAt && c.expiresOn >= today;
                return (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 90px 1fr auto', gap: 12, alignItems: 'center', fontSize: 13, color: live ? T.text : T.textSub }}>
                    <b>{c.customerName}</b><span style={{ color: T.textSub }}>{c.bikeName}</span><span>{money(c.amount)}</span>
                    <span style={{ color: !live ? T.textSub : left <= 7 ? '#c0661a' : T.textSub }}>
                      {c.usedAt ? `used ${lab(String(c.usedAt).slice(0, 10), { month: 'short', day: 'numeric' })}${c.usedNote ? ` on ${c.usedNote}` : ''}`
                        : !live ? `ended ${lab(c.expiresOn, { month: 'short', day: 'numeric' })}, kept`
                        : `counts toward a bike until ${lab(c.expiresOn, { month: 'short', day: 'numeric' })}${left <= 7 ? `, ${left} day${left === 1 ? '' : 's'} left` : ''}`}
                    </span>
                    {live ? <button type="button" disabled={busy} style={btn(false, { fontSize: 12, padding: '4px 10px' })}
                      onClick={() => { const note = window.prompt('Used on which ticket or sale? (optional)', ''); if (note === null) return; run(() => api('PUT', `/book/rent/staff/demo-credits/${c.id}/used`, { note })); }}>Mark used</button> : <span />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {detail}
      </div>

      {modal && modal.kind === 'checkin' && <CheckIn T={T} r={modal.r} api={api} onClose={() => setModal(null)} onDone={async (warn) => { setModal(null); if (warn) setError(warn); await load(); }} />}
      {modal && modal.kind === 'dates' && <ChangeDates T={T} r={modal.r} api={api} onClose={() => setModal(null)} onDone={async () => { setModal(null); await load(); }} />}
      {modal && modal.kind === 'book' && <BookRental T={T} isDark={isDark} data={data} api={api} onClose={() => setModal(null)} onDone={async (id) => { setModal(null); await load(); setSelectedId(id); }} />}
      {modal && modal.kind === 'contact' && <ContactEdit T={T} booking={modal.r} onClose={() => setModal(null)} onSaved={() => load()} />}
      {modal && modal.kind === 'ticket' && <TicketDetail ticketId={modal.r.workorderId} onClose={() => { setModal(null); load(); }} theme={theme} currentUser={currentUser} />}
      {modal && modal.kind === 'text' && (
        <Modal T={T} title={`Text ${modal.r.customerName}`} onClose={() => setModal(null)} width={520}>
          <div style={{ height: 460, display: 'flex', flexDirection: 'column' }}>
            <SmsConversation phone={modal.r.customerPhone} ticketId={modal.r.workorderId} customerId={modal.r.lsCustomerId || null}
              customerName={modal.r.customerName} bikeLabel={modal.r.bikes.map(b => b.name).join(', ')} balance={0} theme={theme} markReadOnOpen={true} />
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ T, title, children, onClose, width = 460 }) {
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,10,20,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{title}</div>
          <button type="button" onClick={onClose} title="Close" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = (T) => ({ width: '100%', background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 10px', color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' });

function CheckIn({ T, r, api, onClose, onDone }) {
  const [damaged, setDamaged] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hasHold = ['held', 'charged'].includes(r.hold.status);
  const go = async () => {
    setBusy(true); setError('');
    try {
      const d = await api('POST', `/book/rent/staff/${r.id}/checkin`, damaged ? { damage: Number(amount), note } : {});
      onDone(d.warning || null);
    } catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal T={T} title={`Check in ${r.customerName}'s bikes`} onClose={onClose}>
      <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12, lineHeight: 1.5 }}>{r.bikes.map(b => b.name).join(', ')}</div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, color: T.text, marginBottom: 8, cursor: 'pointer' }}>
        <input type="radio" checked={!damaged} onChange={() => setDamaged(false)} style={{ accentColor: '#e67e22' }} />
        All good{hasHold ? `: ${r.hold.mode === 'charge' ? 'refund' : 'release'} the ${money(r.hold.amount)}` : ''}
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, color: hasHold ? T.text : T.textSub, marginBottom: 8, cursor: hasHold ? 'pointer' : 'default' }}>
        <input type="radio" disabled={!hasHold} checked={damaged} onChange={() => setDamaged(true)} style={{ accentColor: '#e67e22' }} />
        Damage: keep part or all of the {r.hold.mode === 'charge' ? 'deposit' : 'hold'}{!hasHold ? ' (no hold was placed: ring it up at the register)' : ''}
      </label>
      {damaged && (
        <div style={{ display: 'grid', gap: 8, margin: '6px 0 4px 24px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ color: T.textSub }}>$</span>
            <input type="number" min="0" max={r.hold.amount} step="0.01" value={amount} onChange={e => setAmount(e.target.value)} style={{ ...inputStyle(T), width: 120 }} />
            <span style={{ fontSize: 12, color: T.textSub }}>of {money(r.hold.amount)}; the rest is {r.hold.mode === 'charge' ? 'refunded' : 'released'}</span>
          </div>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="What is the damage? Goes on the ticket." style={inputStyle(T)} />
          <div style={{ fontSize: 12, color: T.textSub }}>The amount kept goes on the customer's Lightspeed account, ready to put toward the repair.</div>
        </div>
      )}
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={busy} onClick={go} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>{busy ? 'Working…' : 'Check in'}</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}

function ChangeDates({ T, r, api, onClose, onDone }) {
  const [start, setStart] = useState(r.start);
  const [end, setEnd] = useState(r.end);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    if (!start || !end || end < start) { setCheck(null); return; }
    api('GET', `/book/rent/staff/availability?start=${start}&end=${end}&exclude=${r.id}`).then(d => { if (live) setCheck(d); }).catch(() => {});
    return () => { live = false; };
  }, [start, end]); // api closes over constants only
  const clash = check && check.bikes ? r.bikes.filter(b => !(check.bikes.find(x => x.id === b.bikeId) || {}).free) : [];
  const go = async () => {
    setBusy(true); setError('');
    try { const d = await api('PUT', `/book/rent/staff/${r.id}/dates`, { start, end }); toastNotified(r.customerName, d.notified, 'the new dates'); onDone(); }
    catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal T={T} title={`Change ${r.customerName}'s dates`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ fontSize: 12, color: T.textSub }}>Pick up<input type="date" value={start} disabled={r.status === 'out'} onChange={e => setStart(e.target.value)} style={inputStyle(T)} /></label>
        <label style={{ fontSize: 12, color: T.textSub }}>Return<input type="date" value={end} onChange={e => setEnd(e.target.value)} style={inputStyle(T)} /></label>
      </div>
      <div style={{ fontSize: 13, marginTop: 10, color: clash.length ? '#e74c3c' : T.textSub }}>
        {end < start ? 'The return is before the pick-up.' : !check ? 'Checking…'
          : clash.length ? `Not free then: ${clash.map(b => b.name).join(', ')}.`
          : `${check.days} day${check.days === 1 ? '' : 's'}${check.charge && check.charge.weekend ? ', the weekend: models with a weekend item get it instead of the daily one' : check.charge && check.charge.chargeDays !== check.days ? `, ${check.charge.chargeDays} charged` : ''}. The ticket's lines and total change to match, and the customer gets a text.${check.closed ? ' Note: the shop is normally closed on one of those days.' : ''}`}
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={busy || !check || clash.length > 0 || end < start} onClick={go} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy || clash.length ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Save and text them'}</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}

function BookRental({ T, isDark, data, api, onClose, onDone }) {
  const [start, setStart] = useState(data.today);
  const [end, setEnd] = useState(addDays(data.today, 1));
  const [check, setCheck] = useState(null);
  const [picked, setPicked] = useState([]);
  const [riders, setRiders] = useState({});
  const [form, setForm] = useState({ customerName: '', customerPhone: '', customerEmail: '', notes: '' });
  const [customer, setCustomer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    if (!start || !end || end < start) { setCheck(null); return; }
    api('GET', `/book/rent/staff/availability?start=${start}&end=${end}`).then(d => { if (live) { setCheck(d); setPicked(p => p.filter(id => (d.bikes.find(b => b.id === id) || {}).free)); } }).catch(() => {});
    return () => { live = false; };
  }, [start, end]); // api closes over constants only
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const go = async () => {
    setError('');
    if (!form.customerName.trim()) return setError('Customer name is required.');
    if (!picked.length) return setError('Pick at least one bike.');
    setBusy(true);
    try {
      const d = await api('POST', '/book/rent/staff/create', { ...form, start, end, bikes: picked.map(id => ({ bikeId: id, rider: { name: riders[id] || '' } })), ...(customer?.id ? { lsCustomerId: String(customer.id) } : {}) });
      onDone(d.id);
    } catch (err) { setError(err.message); setBusy(false); }
  };
  const days = start && end && end >= start ? dayCount(start, end) : 0;
  return (
    <Modal T={T} title="Book a rental" onClose={onClose} width={640}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12, lineHeight: 1.5 }}>Creates the Lightspeed customer and ticket like an online booking, with a line per bike, and texts the customer a confirmation. No payment is taken now.</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: T.textSub }}>Pick up<input type="date" value={start} onChange={e => setStart(e.target.value)} style={inputStyle(T)} /></label>
        <label style={{ fontSize: 12, color: T.textSub }}>Return<input type="date" value={end} onChange={e => setEnd(e.target.value)} style={inputStyle(T)} /></label>
      </div>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{days ? `${days} day${days === 1 ? '' : 's'}` : 'Pick the dates'}{check && check.closed ? ' · the shop is normally closed on one of those days' : ''}</div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {(check?.bikes || []).map(b => {
          const sel = picked.includes(b.id);
          return (
            <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', border: `1px solid ${sel ? '#e67e22' : T.cardBorder}`, borderRadius: 8, padding: '6px 10px', opacity: b.free ? 1 : 0.5 }}>
              <input type="checkbox" disabled={!b.free} checked={sel} onChange={() => setPicked(p => sel ? p.filter(x => x !== b.id) : [...p, b.id])} style={{ accentColor: '#e67e22' }} />
              <span style={{ fontSize: 13, color: T.text, flex: 1 }}>{b.name}{b.size ? ` · ${b.size}` : ''}{b.demo ? ' · demo' : ''} <span style={{ color: T.textSub }}>{b.free ? `$${b.rate}/day` : b.why}</span></span>
              {sel && <input value={riders[b.id] || ''} placeholder="Rider" onChange={e => setRiders(x => ({ ...x, [b.id]: e.target.value }))} style={{ ...inputStyle(T), width: 160, padding: '5px 8px' }} />}
            </div>
          );
        })}
      </div>
      <CustomerMatch T={T} isDark={isDark} input={inputStyle(T)} form={form} setForm={setForm} selected={customer} onSelect={setCustomer} />
      <input style={inputStyle(T)} placeholder="Notes" value={form.notes} onChange={set('notes')} />
      <NoPhoneHint phone={form.customerPhone} />
      {error && <div role="alert" style={{ fontSize: 13, color: '#e74c3c', marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" disabled={busy} onClick={go} style={{ ...S.btn(true), background: '#e67e22', color: '#fff', opacity: busy ? 0.6 : 1 }}>{busy ? 'Booking…' : 'Book it'}</button>
        <button type="button" onClick={onClose} style={{ ...S.btn(false), color: T.textSub, borderColor: T.inputBorder }}>Cancel</button>
      </div>
    </Modal>
  );
}
